import React, { useEffect, useMemo, useRef, useState } from 'react';
import './EntityPicker.css';

export type EntityPickerOption = {
  id: number;
  label: string;
  sublabel?: string;
  searchText: string;
};

type EntityPickerInputProps = {
  id: string;
  value: string;
  options: EntityPickerOption[];
  placeholder?: string;
  createLabel?: string;
  maxOptions?: number;
  onChange: (value: string) => void;
  onSelect: (option: EntityPickerOption) => void;
  onCreateClick: (prefill: string) => void;
  inputClassName?: string;
};

const EntityPickerInput: React.FC<EntityPickerInputProps> = ({
  id,
  value,
  options,
  placeholder,
  createLabel = '+ Создать',
  maxOptions = 5,
  onChange,
  onSelect,
  onCreateClick,
  inputClassName = 'entity-picker-input ac-input',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    const normalized = value.trim().toLowerCase();
    const sorted = [...options];
    if (!normalized) return sorted.slice(0, maxOptions);
    return sorted
      .filter((opt) => opt.searchText.toLowerCase().includes(normalized))
      .slice(0, maxOptions);
  }, [maxOptions, options, value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const openDropdown = () => {
    setIsOpen(true);
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      openDropdown();
      return;
    }
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (filteredOptions.length === 0) return;
        const next = highlightedIndex + 1;
        setHighlightedIndex(next >= filteredOptions.length ? 0 : next);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (filteredOptions.length === 0) return;
        const prev = highlightedIndex - 1;
        setHighlightedIndex(prev < 0 ? filteredOptions.length - 1 : prev);
        break;
      }
      case 'Enter': {
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          e.preventDefault();
          onSelect(filteredOptions[highlightedIndex]);
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
        break;
      }
      case 'Escape': {
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="entity-picker-wrapper" ref={wrapperRef}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={openDropdown}
        onClick={openDropdown}
        onKeyDown={handleKeyDown}
        className={inputClassName}
      />
      {isOpen && (
        <ul className="entity-picker-list" role="listbox" aria-labelledby={id}>
          <li className="entity-picker-create entity-picker-create-top">
            <button
              type="button"
              className="entity-picker-create-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onCreateClick(value.trim());
                setIsOpen(false);
                setHighlightedIndex(-1);
              }}
            >
              {createLabel}
            </button>
          </li>
          {filteredOptions.length === 0 && (
            <li className="entity-picker-empty">Совпадений не найдено</li>
          )}
          {filteredOptions.map((opt, idx) => (
            <li
              key={opt.id}
              role="option"
              aria-selected={idx === highlightedIndex}
              className={`entity-picker-item ${idx === highlightedIndex ? 'active' : ''}`}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(opt);
                setIsOpen(false);
                setHighlightedIndex(-1);
                inputRef.current?.focus();
              }}
            >
              <span>{opt.label}</span>
              {opt.sublabel && <span className="entity-picker-item-sub">{opt.sublabel}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EntityPickerInput;
